import { useFrame, useThree } from '@react-three/fiber';
import { useEffect, useMemo, useRef } from 'react';
import {
  AdditiveBlending,
  BoxGeometry,
  Color,
  CylinderGeometry,
  DoubleSide,
  ExtrudeGeometry,
  MeshStandardMaterial,
  Quaternion,
  Shape,
  ShapeGeometry,
  TorusGeometry,
  Vector3,
  type Group,
  type Sprite,
} from 'three';
import type { Building, RegionTree } from '@map/shared';
import { containsPoint, type Territory } from '@map/worldkit';
import { isTouch } from '../lib/useIsMobile.js';
import { prefersReducedMotion, sceneColor, type TokenName } from '../styles/tokens.js';
import { frameOf, type Frame } from './ArabicLand.js';
import { glowTexture } from './planetSkin.js';

/**
 * Books read (Growth › Knowledge › Books): an award medal for every book,
 * standing in its city in rows in the order they were finished, like a trophy
 * cabinet.
 *
 * The face's metal is the rating — bronze for one or two stars, silver for
 * three, gold for four, and a shining gold for five — and the cut ribbon tails
 * are the city's colour. Each medal stands on a little post and turns to face
 * the camera, so it can be read from orbit. Books never weather.
 *
 * Clicking is judged on screen like the people and the camels.
 */
export interface MedalsProps {
  territories: Map<string, Territory>;
  tree: RegionTree;
  buildings: Building[];
  onOpen?: (regionId: string, buildingId: string) => void;
}

export type Metal = 'bronze' | 'silver' | 'gold' | 'bright';

/** Whole stars to the face's metal. */
export function metalOf(rating: number): Metal {
  if (rating >= 5) return 'bright';
  if (rating >= 4) return 'gold';
  if (rating >= 3) return 'silver';
  return 'bronze';
}

const RIBBONS: TokenName[] = ['ribbon-1', 'ribbon-2', 'ribbon-3', 'ribbon-4', 'ribbon-5', 'ribbon-6'];

/** Land is drawn at 1.02; a medal's foot sits a hair above it. */
const GROUND = 1.0205;
const REACH_MIN = 14;
const REACH_MAX = 70;

interface Medal {
  id: string;
  regionId: string;
  metal: Metal;
  ribbon: TokenName;
  up: Vector3;
  /** Whole height, foot to the top of the face, in world units. */
  height: number;
}

/**
 * Where each of a city's books stands: a grid of rows across the middle of the
 * city, oldest at the back-left, filled row by row. The grid shrinks until
 * every spot is on the city's own land.
 */
export function cabinet(frame: Frame, count: number): { spots: Vector3[]; height: number } {
  const cols = Math.max(1, Math.ceil(Math.sqrt(count * 1.6)));
  const rows = Math.max(1, Math.ceil(count / cols));
  let gap = Math.sqrt(frame.territory.area / Math.max(1, count)) * 0.75;
  let spots: Vector3[] = [];
  for (let attempt = 0; attempt < 12; attempt += 1) {
    spots = [];
    for (let i = 0; i < count; i += 1) {
      const row = Math.floor(i / cols);
      const inRow = Math.min(cols, count - row * cols);
      const x = ((i % cols) - (inRow - 1) / 2) * gap;
      const y = ((rows - 1) / 2 - row) * gap;
      spots.push(frame.up.clone().addScaledVector(frame.east, x).addScaledVector(frame.north, y).normalize());
    }
    if (spots.every((s) => containsPoint(frame.territory.ring, { x: s.x, y: s.y, z: s.z }))) break;
    gap *= 0.85;
  }
  return { spots, height: Math.min(0.045, Math.max(0.01, gap * 1.25)) };
}

export function Medals({ territories, tree, buildings, onOpen }: MedalsProps) {
  const medals = useMemo(() => {
    const byCity = new Map<string, Building[]>();
    for (const b of buildings) {
      if (b.kind !== 'book' || !b.book || !territories.has(b.regionId)) continue;
      const list = byCity.get(b.regionId) ?? [];
      list.push(b);
      byCity.set(b.regionId, list);
    }
    const out: Medal[] = [];
    for (const [regionId, books] of byCity) {
      const territory = territories.get(regionId);
      if (!territory) continue;
      books.sort((a, b) => (a.book?.order ?? 0) - (b.book?.order ?? 0));
      // The ribbon is the city's colour: its place among the shelf's cities.
      const region = tree.regions[regionId];
      const siblings = region?.parentId ? (tree.regions[region.parentId]?.children ?? []) : [];
      const ribbon = RIBBONS[Math.max(0, siblings.indexOf(regionId)) % RIBBONS.length] as TokenName;
      const { spots, height } = cabinet(frameOf(territory), books.length);
      books.forEach((b, i) => {
        const up = spots[i];
        if (!up || !b.book) return;
        out.push({ id: b.id, regionId, metal: metalOf(b.book.rating), ribbon, up, height });
      });
    }
    return out;
  }, [buildings, territories, tree]);

  const kit = useKit();
  const root = useRef<Group>(null);
  usePicking(medals, root, onOpen);

  if (medals.length === 0) return null;
  return (
    <group ref={root}>
      {medals.map((medal) => (
        <MedalFigure key={medal.id} medal={medal} kit={kit} root={root} />
      ))}
    </group>
  );
}

/** Geometry and materials, shared by every medal; a medal is these at its own scale. */
function useKit() {
  const kit = useMemo(() => {
    // Built for a medal one unit tall, standing on y = 0 and facing +z.
    const face = new CylinderGeometry(0.21, 0.21, 0.04, 40).rotateX(Math.PI / 2);
    const rim = new TorusGeometry(0.21, 0.018, 8, 40);
    const star = new Shape();
    for (let i = 0; i < 10; i += 1) {
      const a = Math.PI / 2 + (i * Math.PI) / 5;
      const r = i % 2 === 0 ? 0.12 : 0.05;
      if (i === 0) star.moveTo(Math.cos(a) * r, Math.sin(a) * r);
      else star.lineTo(Math.cos(a) * r, Math.sin(a) * r);
    }
    star.closePath();
    const emboss = new ExtrudeGeometry(star, { depth: 0.015, bevelEnabled: false });
    // A ribbon tail: a strip with a V cut out of its end.
    const tail = new Shape();
    const w = 0.045;
    const len = 0.34;
    tail.moveTo(-w, 0);
    tail.lineTo(w, 0);
    tail.lineTo(w, -len);
    tail.lineTo(0, -len + 0.06);
    tail.lineTo(-w, -len);
    tail.closePath();
    const ribbon = new ShapeGeometry(tail);
    const post = new CylinderGeometry(0.022, 0.028, 1, 8);
    const foot = new BoxGeometry(0.2, 0.03, 0.12);

    const metal = (token: TokenName, fallback: string, glow: number) => {
      const colour = new Color(sceneColor(token, fallback));
      return new MeshStandardMaterial({
        color: colour,
        // No environment to reflect, so mostly lit colour with a little sheen,
        // and a touch of its own light so it still reads as metal far off.
        metalness: 0.35,
        roughness: 0.35,
        emissive: colour,
        emissiveIntensity: glow,
      });
    };
    const metals: Record<Metal, MeshStandardMaterial> = {
      bronze: metal('medal-bronze', '#a8703a', 0.12),
      silver: metal('medal-silver', '#c9ccd1', 0.12),
      gold: metal('medal-gold', '#e0b33c', 0.15),
      bright: metal('medal-bright', '#ffd84a', 0.45),
    };
    const ribbons = Object.fromEntries(
      RIBBONS.map((t) => [t, new MeshStandardMaterial({ color: new Color(sceneColor(t, '#c04040')), roughness: 0.8, side: DoubleSide })]),
    ) as Record<TokenName, MeshStandardMaterial>;
    const stand = new MeshStandardMaterial({ color: new Color(sceneColor('medal-post', '#6b5a44')), roughness: 0.9 });
    const glow = glowTexture();
    const halo = new Color(sceneColor('medal-bright', '#ffd84a'));
    return { face, rim, emboss, ribbon, post, foot, metals, ribbons, stand, glow, halo };
  }, []);
  useEffect(
    () => () => {
      for (const g of [kit.face, kit.rim, kit.emboss, kit.ribbon, kit.post, kit.foot]) g.dispose();
      for (const m of [...Object.values(kit.metals), ...Object.values(kit.ribbons), kit.stand]) m.dispose();
      kit.glow?.dispose();
    },
    [kit],
  );
  return kit;
}

type Kit = ReturnType<typeof useKit>;
const Y = new Vector3(0, 1, 0);
/** The medal's face hangs this high up its post, as a share of its height. */
const FACE_AT = 0.76;
/** How far back the face may lean toward a camera overhead, in radians. */
const MAX_LEAN = 1.25;

function MedalFigure({ medal, kit, root }: { medal: Medal; kit: Kit; root: React.RefObject<Group | null> }) {
  const { position, quaternion, inverse } = useMemo(() => {
    const quaternion = new Quaternion().setFromUnitVectors(Y, medal.up);
    return { position: medal.up.clone().multiplyScalar(GROUND), quaternion, inverse: quaternion.clone().invert() };
  }, [medal.up]);
  const turn = useRef<Group>(null);
  const tilt = useRef<Group>(null);
  const shine = useRef<Sprite>(null);
  const camera = useThree((s) => s.camera);
  const invalidate = useThree((s) => s.invalidate);
  const still = prefersReducedMotion();
  const local = useMemo(() => new Vector3(), []);

  // Turn about its own post to face the camera, wherever the world has spun to.
  useFrame((state) => {
    if (turn.current) {
      local.copy(camera.position);
      if (root.current) root.current.worldToLocal(local);
      local.sub(position).applyQuaternion(inverse);
      turn.current.rotation.y = Math.atan2(local.x, local.z);
      // Seen from high above, an upright face would be edge-on: lean it back
      // by the camera's elevation, short of lying flat.
      if (tilt.current) tilt.current.rotation.x = -Math.min(MAX_LEAN, Math.max(0, Math.atan2(local.y - medal.height * FACE_AT, Math.hypot(local.x, local.z))));
    }
    if (shine.current) {
      const breath = still ? 0.5 : 0.5 + Math.sin(state.clock.elapsedTime * 1.6 + medal.up.x * 40) * 0.5;
      shine.current.scale.setScalar(medal.height * (0.75 + breath * 0.25));
      (shine.current.material as { opacity: number }).opacity = 0.45 + breath * 0.35;
      if (!still) invalidate();
    }
  });

  const metal = kit.metals[medal.metal];
  const ribbon = kit.ribbons[medal.ribbon];
  const faceY = FACE_AT;
  return (
    <group position={position} quaternion={quaternion} scale={medal.height}>
      <group ref={turn}>
        <mesh geometry={kit.foot} material={kit.stand} position={[0, 0.015, 0]} />
        <mesh geometry={kit.post} material={kit.stand} position={[0, faceY / 2, -0.04]} scale={[1, faceY, 1]} />
        {/* The face leans back to look up at the camera; the post stays upright. */}
        <group ref={tilt} position={[0, faceY, 0]}>
          {/* The cut tails hang from behind the face in a V. */}
          <mesh geometry={kit.ribbon} material={ribbon} position={[-0.03, 0, -0.025]} rotation={[0, 0, -0.38]} />
          <mesh geometry={kit.ribbon} material={ribbon} position={[0.03, 0, -0.025]} rotation={[0, 0, 0.38]} />
          <mesh geometry={kit.face} material={metal} />
          <mesh geometry={kit.rim} material={metal} />
          <mesh geometry={kit.emboss} material={metal} position={[0, -0.005, 0.02]} />
          {medal.metal === 'bright' && kit.glow && (
            <sprite ref={shine} position={[0, 0, -0.06]}>
              <spriteMaterial map={kit.glow} color={kit.halo} blending={AdditiveBlending} depthWrite={false} transparent toneMapped={false} />
            </sprite>
          )}
        </group>
      </group>
    </group>
  );
}

/** A click (not a drag) near a medal's face opens its book. */
function usePicking(medals: Medal[], root: React.RefObject<Group | null>, onOpen: MedalsProps['onOpen']) {
  const { camera, gl } = useThree();
  useEffect(() => {
    if (!onOpen || medals.length === 0) return;
    const canvas = gl.domElement;
    const screen = new Vector3();
    const edge = new Vector3();
    const toCamera = new Vector3();
    const world = new Vector3();
    const faces = medals.map((m) => m.up.clone().multiplyScalar(GROUND + m.height * FACE_AT));
    const pick = (event: PointerEvent, generosity: number): Medal | null => {
      const rect = canvas.getBoundingClientRect();
      const px = event.clientX - rect.left;
      const py = event.clientY - rect.top;
      let best: Medal | null = null;
      let bestDistance = Infinity;
      faces.forEach((local, i) => {
        const medal = medals[i];
        if (!medal) return;
        const position = root.current ? world.copy(local).applyMatrix4(root.current.matrixWorld) : local;
        toCamera.copy(camera.position).sub(position);
        if (toCamera.dot(position) <= 0) return;
        screen.copy(position).project(camera);
        const sx = ((screen.x + 1) / 2) * rect.width;
        const sy = ((1 - screen.y) / 2) * rect.height;
        edge.copy(position).addScaledVector(position, (medal.height * 0.3) / position.length()).project(camera);
        const ex = ((edge.x + 1) / 2) * rect.width;
        const ey = ((1 - edge.y) / 2) * rect.height;
        const reach = Math.min(REACH_MAX, Math.max(REACH_MIN, Math.hypot(ex - sx, ey - sy) * 1.4)) * generosity;
        const d = Math.hypot(sx - px, sy - py);
        if (d < reach && d < bestDistance) {
          bestDistance = d;
          best = medal;
        }
      });
      return best;
    };
    let from: { x: number; y: number; id: number } | null = null;
    const onDown = (e: PointerEvent) => (from = { x: e.clientX, y: e.clientY, id: e.pointerId });
    const onUp = (e: PointerEvent) => {
      const began = from;
      from = null;
      if (!began || began.id !== e.pointerId || Math.hypot(e.clientX - began.x, e.clientY - began.y) > 8) return;
      const medal = pick(e, isTouch() ? 2 : 1.3);
      if (medal) onOpen(medal.regionId, medal.id);
    };
    const onCancel = () => (from = null);
    canvas.addEventListener('pointerdown', onDown);
    canvas.addEventListener('pointerup', onUp);
    canvas.addEventListener('pointercancel', onCancel);
    return () => {
      canvas.removeEventListener('pointerdown', onDown);
      canvas.removeEventListener('pointerup', onUp);
      canvas.removeEventListener('pointercancel', onCancel);
    };
  }, [medals, root, camera, gl, onOpen]);
}
